import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, PhoneCall, Bot, Cpu, LayoutDashboard, Fingerprint, ArrowRight } from "lucide-react";
import { track } from "../../lib/analytics";

const RISKS = [
  { id: "chatbot", icon: MessageSquare, label: "Customer chatbot", statement: "I want customers to use AI safely.", product: "norse3 Vör", desc: "A governed AI chat widget that applies your policy in real time, escalates to people and captures evidence.", to: "/vor", path: ["Customer question", "Policy & risk check", "Allow / Hold / Escalate", "Evidence"] },
  { id: "calls", icon: PhoneCall, label: "Regulated calls", statement: "I need better visibility across customer conversations.", product: "norse3 Lögberg", desc: "Screen customer conversations against risk indicators and prioritise interactions for human review.", to: "/logberg", path: ["Call / transcript", "Screen for indicators", "Prioritise & route", "Reviewer decision"] },
  { id: "employee", icon: Bot, label: "Employee AI", statement: "I need to control how staff use AI.", product: "norse3 Enterprise AI", desc: "Runtime control around how staff use approved AI — inputs, outputs and actions, by policy.", to: "/enterprise-ai", path: ["Employee prompt", "Inspect input & output", "Warn / Redact / Block", "Evidence"] },
  { id: "agents", icon: Cpu, label: "AI agents", statement: "I need controls before AI performs actions.", product: "norse3 Enterprise AI / Platform", desc: "Evaluate proposed agent actions against policy and hold high-impact actions for authorisation.", to: "/enterprise-ai", path: ["Proposed action", "Policy evaluation", "Permit / Hold / Block", "Approval & evidence"] },
  { id: "governance", icon: LayoutDashboard, label: "AI governance", statement: "I need central visibility, policy and evidence.", product: "Heimdall / norse3 Platform", desc: "One place to manage policy, triage incidents, review interactions and produce management information.", to: "/platform", path: ["Interactions", "Policy & triage", "Human review", "Management information"] },
  { id: "sensitive", icon: Fingerprint, label: "Sensitive information", statement: "I need to prevent inappropriate information entering or leaving AI.", product: "Aegis + Enterprise AI", desc: "Detect designated sensitive content and apply configured actions before it reaches or leaves the model.", to: "/enterprise-ai", path: ["Content submitted", "Detect sensitive data", "Redact / Block / Escalate", "Record reference"] },
];

export default function RiskSelector() {
  const [active, setActive] = useState(RISKS[0].id);
  const cur = RISKS.find((r) => r.id === active);
  const Icon = cur.icon;

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4" data-testid="fs-risk-selector">
      <div className="flex flex-col gap-1.5">
        {RISKS.map((r) => {
          const RIcon = r.icon;
          const on = r.id === active;
          return (
            <button
              key={r.id}
              onClick={() => { setActive(r.id); track("fs_risk_selected", { risk: r.id }); }}
              data-testid={`fs-risk-${r.id}`}
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${on ? "border-primary/40 bg-primary/[0.07]" : "border-white/10 bg-navy-900/60 hover:border-white/20"}`}
            >
              <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${on ? "bg-primary/15 text-primary" : "bg-white/[0.04] text-muted-foreground"}`}><RIcon className="h-5 w-5" /></span>
              <span className={`text-sm font-medium ${on ? "text-foreground" : "text-muted-foreground"}`}>{r.label}</span>
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
          <div>
            <p className="text-sm text-muted-foreground">{cur.statement}</p>
            <h3 className="text-xl font-bold text-foreground">{cur.product}</h3>
          </div>
        </div>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed">{cur.desc}</p>
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {cur.path.map((p, i) => (
            <React.Fragment key={p}>
              <span className="rounded-lg border border-white/10 bg-navy-800/50 px-3 py-2 text-sm text-foreground/90">{p}</span>
              {i < cur.path.length - 1 && <ArrowRight className="h-4 w-4 text-muted-foreground/40" />}
            </React.Fragment>
          ))}
        </div>
        <Link to={cur.to} onClick={() => track("fs_risk_cta", { risk: cur.id })} data-testid="fs-risk-cta" className="mt-7 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90">
          Explore {cur.product} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
